import * as React from "react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Table, Button, Input, Modal, Typography, Spin, Space } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import EntityInfo from "../../types/EntityInfo";
import { deleteCity, editCity, fetchCities, postCity } from "../../api/city";
import { operationNames, userHasAccess } from "../../data/pages";
import { useTexts } from "../../hooks/useTexts";
import CityModal from "./CityModal";
import TablePagination from "../TablePagination/TablePagination";

const { Title, Text } = Typography;

const Cities = () => {
    const { t } = useTexts();
    const [cities, setCities] = useState<EntityInfo[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [searchName, setSearchName] = useState("");
    const [inputValue, setInputValue] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedCity, setSelectedCity] = useState<EntityInfo | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<EntityInfo>({ id: 0, name: '' });

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);

    const canCreate = userHasAccess(operationNames.CREATE_CITY);
    const canEdit = userHasAccess(operationNames.EDIT_CITY);
    const canDelete = userHasAccess(operationNames.DELETE_CITY);

    const getCities = (currentName = searchName) => {
        setLoading(true);
        fetchCities({ name: currentName }).then(response => {
            setCities(response?.items || []);
        })
            .catch(error => console.error("ERROR", error))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        getCities(searchName);
    }, [searchName]);

    const openAddModal = () => {
        setErrors([]);
        setSelectedCity(null);
        setModalOpen(true);
    }

    const openEditModal = (city: EntityInfo) => {
        setErrors([]);
        setSelectedCity(city);
        setModalOpen(true);
    }

    const closeModal = () => {
        setErrors([]);
        setSelectedCity(null);
        setModalOpen(false);
    }

    const handleSave = (name: string): void => {
        setErrors([]);
        setSubmitting(true);
        const request = { name };
        const isEdit = !!selectedCity;
        const action = isEdit ? editCity(selectedCity.id, request) : postCity(request);
        action
            .then(response => {
                if (!!response?.messages?.length && response.messages.length > 0) {
                    setErrors(response.messages);
                } else if (response?.message) {
                    setErrors([response.message]);
                } else {
                    getCities();
                    toast.success(t('city', isEdit ? 'cityUpdated' : 'cityAdded'), { theme: "colored" });
                    closeModal();
                }
            })
            .catch(error => console.error("ERROR", error))
            .finally(() => setSubmitting(false));
    };

    const removeCity = (id: number) => {
        deleteCity(id).then(response => {
            if (!response?.message) {
                getCities();
            }
            setDeleteOpen(false);
        })
            .catch(error => console.error("ERROR", error));
    }

    const columns: ColumnsType<EntityInfo> = [
        {
            title: '#',
            key: 'index',
            render: (text, record, index) => index + 1,
            width: 60,
        },
        {
            title: t('city', 'cityName'),
            dataIndex: 'name',
            key: 'name',
        }
    ];

    if (canEdit || canDelete) {
        columns.push({
            title: '',
            key: 'action',
            width: 220,
            render: (_, record) => (
                <Space>
                    {canEdit && (
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => openEditModal(record)}
                        >
                            {t('common', 'edit')}
                        </Button>
                    )}
                    {canDelete && (
                        <Button
                            danger
                            type="primary"
                            icon={<DeleteOutlined />}
                            onClick={() => {
                                setRowToDelete(record);
                                setDeleteOpen(true);
                            }}
                        >
                            {t('common', 'delete')}
                        </Button>
                    )}
                </Space>
            )
        });
    }

    const total = cities.length;
    const pageCount = Math.ceil(total / limit) || 1;
    const currentCities = cities.slice((page - 1) * limit, page * limit);

    return (
        <div style={{ marginTop: '50px' }}>
            <Modal
                title={`'${rowToDelete?.name}' ${t('product', 'deleteConfirm')}`}
                open={deleteOpen}
                onCancel={() => {
                    setRowToDelete({ id: 0, name: '' });
                    setDeleteOpen(false);
                }}
                onOk={() => removeCity(rowToDelete?.id)}
                okText={t('common', 'yes')}
                cancelText={t('common', 'close')}
                okButtonProps={{ danger: true }}
            >
                <Text>{t('city', 'deleteConfirm', { name: rowToDelete?.name })}</Text>
            </Modal>

            <CityModal
                open={modalOpen}
                city={selectedCity}
                submitting={submitting}
                errors={errors}
                onClose={closeModal}
                onSave={handleSave}
            />

            <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center' }}>
                <Input.Search
                    placeholder={t('city', 'searchByName')}
                    allowClear
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onSearch={(value) => setSearchName(value)}
                    style={{ width: 250, marginRight: 8 }}
                />
                <Button 
                    danger 
                    icon={<DeleteOutlined />} 
                    onClick={() => {
                        setInputValue("");
                        setSearchName("");
                    }} 
                    style={{ marginRight: 24 }} 
                    title={t('common', 'clearFilter') || "Clear Filter"}
                />
                {canCreate && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
                        {t('city', 'addCity')}
                    </Button>
                )}
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: "50px" }}>
                    <Spin size="large" />
                </div>
            ) : cities?.length > 0 ? (
                <div style={{ background: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                    <Table
                        columns={columns}
                        dataSource={currentCities}
                        rowKey="id"
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                    />
                    <TablePagination
                        current={page}
                        pageSize={limit}
                        pageCount={pageCount}
                        total={total}
                        onChange={(p, s) => {
                            setPage(p);
                            if (s !== limit) setLimit(s);
                        }}
                    />
                </div>
            ) : (
                <Title level={4}>{t('city', 'cityNotFound')}</Title>
            )}
        </div>
    )
}

export default Cities;
